import { AfterViewInit, Component, ViewChild, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { FormGroup, FormBuilder, Validators, FormArray, AbstractControl, FormControl } from '@angular/forms';
import { ComponentFactoryResolver, Injector, SimpleChange, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, of, toArray } from 'rxjs';
import { merge, Observable, of as observableOf, pipe } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSelectChange } from '@angular/material/select';
import { ChangeDetectorRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { ZahlungsabgleichService, Zahlung, ZahlungTable, ZahlungFilter, PaymentStatus, ReconciliationStatus } from '../services/zahlung.service';
import { DataChangeService } from './changes-zahlungsabgleich.service';

import { PhutureIavatarComponent } from '../@components/phuture-iavatar/phuture-iavatar.component';
import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { ZahlungReportDialogComponent } from './zahlung-report-dialog/zahlung-report-dialog.component';

import { environment } from './../../environments/environment';

import { AuthQuery } from '@app/auth/auth.query';

// import { JournalService, MailService, Mail } from '@core';

declare var $: any;

export interface EmpFilter {
  name: string;
  options: string[];
  defaultValue: string;
}

export interface filterOption {
  name: string;
  value: string;
  isdefault: boolean;
}

@Component({
  templateUrl: './zahlungsabgleich.component.html',
  styleUrls: [
    './zahlungsabgleich.component.scss',
  ],
})
export class ZahlungsabgleichComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'valuta_datum',
    'betrag', 
    'debtor_name',
    'creditor_reference',
    'aktenzeichen',
    'mandat_betrag_faellig',
    'betrag_differenz',
    'payment_status',
    'reconciliation_status',
    'actions'
  ];

  dataSource: MatTableDataSource<Zahlung>;
  isLoading = false;
  searchValue = '';
  currentFilter = '';

  // Pagination
  pageSize = 25;
  pageIndex = 0;
  totalCount = 0;
  totalPages = 0;

  // Sorting
  sortField = 'valuta_datum';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Filtering
  filterForm: FormGroup;
  availableFilters: EmpFilter[] = [];
  selectedFilters: filterOption[] = [];

  // Data
  zahlungen: Zahlung[] = [];
  current_aktenzeichen = '';
  selectedZahlung: Zahlung | null = null;

  // Filter toggle
  isFilterExpanded = false;

  // Open state for the custom Tailwind status dropdowns (only one at a time)
  openStatusDropdown: 'payment' | 'reconciliation' | null = null;

  // Active tab: 'import' shows open/unassigned payments + import tools,
  // 'zahlungen' shows all payments with a status other than offen.
  activeTab: 'import' | 'zahlungen' = 'import';

  // Legend accordion (collapsed by default)
  isLegendExpanded = false;

  // Summary data
  summary = {
    total_betrag: 0,
    total_offen: 0,
    total_abgeschlossen: 0,
    total_probleme: 0
  };

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Payment status options (with explanatory text for the legend)
  paymentStatusOptions = [
    { value: PaymentStatus.UNBEKANNT, label: 'Unbekannt', description: 'Zahlung wurde importiert, konnte aber noch keinem Mandat zugeordnet werden.' },
    { value: PaymentStatus.ZUGEORDNET, label: 'Zugeordnet', description: 'Zahlung wurde einem Mandat zugeordnet, aber noch nicht endgültig geprüft.' },
    { value: PaymentStatus.VOLLSTAENDIG, label: 'Vollständig', description: 'Der bezahlte Betrag entspricht exakt dem fälligen Betrag.' },
    { value: PaymentStatus.TEILWEISE, label: 'Teilweise', description: 'Es wurde nur ein Teil des fälligen Betrags bezahlt.' },
    { value: PaymentStatus.ÜBERZAHLT, label: 'Überzahlt', description: 'Es wurde mehr als der fällige Betrag bezahlt (Differenz positiv).' },
    { value: PaymentStatus.UNTERZAHLT, label: 'Unterzahlt', description: 'Es wurde weniger als der fällige Betrag bezahlt (Differenz negativ).' },
    { value: PaymentStatus.DOPPELT, label: 'Doppelt', description: 'Für dasselbe Mandat wurde mehr als eine Zahlung erfasst.' },
    { value: PaymentStatus.STORNIERT, label: 'Storniert', description: 'Die Zahlung wurde storniert und wird nicht weiter verarbeitet.' },
    { value: PaymentStatus.ZURÜCKGEBUCHT, label: 'Zurückgebucht', description: 'Die Zahlung wurde an den Einzahler retourniert (Rückbuchung).' }
  ];

  // Reconciliation status options (with explanatory text for the legend)
  reconciliationStatusOptions = [
    { value: ReconciliationStatus.OFFEN, label: 'Offen', description: 'Zahlung ist noch nicht bearbeitet bzw. nicht zugeordnet.' },
    { value: ReconciliationStatus.GEPRÜFT, label: 'Geprüft', description: 'Zahlung wurde kontrolliert, der Abgleich ist aber noch nicht abgeschlossen.' },
    { value: ReconciliationStatus.ABGESCHLOSSEN, label: 'Abgeschlossen', description: 'Der Abgleich ist abgeschlossen, das Mandat gilt als bezahlt.' },
    { value: ReconciliationStatus.PROBLEM, label: 'Problem', description: 'Beim Abgleich ist ein Problem aufgetreten (z. B. kein Mandat gefunden oder Betrag weicht ab).' },
    { value: ReconciliationStatus.ZURÜCKGEBUCHT, label: 'Zurückgebucht', description: 'Die Zahlung wurde retourniert, eine camt.054-Datei wurde erstellt.' }
  ];


  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private zahlungsabgleichService: ZahlungsabgleichService,
    private authQuery: AuthQuery,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private dataChangeService: DataChangeService
  ) {
    this.dataSource = new MatTableDataSource<Zahlung>([]);
    this.initializeFilterForm();
  }

  ngOnInit(): void {
    this.initializeFilters();
    this.loadData();
    this.setupDataChangeSubscription();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeFilterForm(): void {
    this.filterForm = this.fb.group({
      dateFrom: [null],
      dateTo: [null],
      paymentStatus: [[]],
      reconciliationStatus: [[]],
      amountMin: [''],
      amountMax: [''],
      debtorName: [''],
      aktenzeichen: ['']
    });
  }

  // ---------------------------------------------------------------------------
  //  Custom Tailwind multi-select dropdowns (Zahlungsstatus / Abgleichstatus)
  // ---------------------------------------------------------------------------

  /** Close any open status dropdown when clicking outside of it. */
  @HostListener('document:click')
  onDocumentClick(): void {
    this.openStatusDropdown = null;
  }

  toggleStatusDropdown(which: 'payment' | 'reconciliation'): void {
    this.openStatusDropdown = this.openStatusDropdown === which ? null : which;
  }

  isStatusSelected(controlName: 'paymentStatus' | 'reconciliationStatus', value: string): boolean {
    const arr = (this.filterForm.get(controlName)?.value as string[]) || [];
    return arr.includes(value);
  }

  toggleStatusValue(controlName: 'paymentStatus' | 'reconciliationStatus', value: string): void {
    const control = this.filterForm.get(controlName);
    const arr = [...((control?.value as string[]) || [])];
    const idx = arr.indexOf(value);
    if (idx >= 0) {
      arr.splice(idx, 1);
    } else {
      arr.push(value);
    }
    control?.setValue(arr);
    this.onFilterChange();
  }

  /** Button label: "Alle" when nothing is selected, else labels or a count. */
  statusSummaryLabel(
    controlName: 'paymentStatus' | 'reconciliationStatus',
    options: { value: string; label: string }[]
  ): string {
    const arr = (this.filterForm.get(controlName)?.value as string[]) || [];
    if (!arr.length) {
      return 'Alle';
    }
    const labels = options.filter(o => arr.includes(o.value)).map(o => o.label);
    if (labels.length <= 2) {
      return labels.join(', ');
    }
    return `${labels.length} ausgewählt`;
  }

  /**
   * Convert a date control value (Date from the Material datepicker, or an ISO
   * string after reset) to the `yyyy-mm-dd` string the backend filter expects.
   */
  private toIsoDate(value: any): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) {
      return '';
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private initializeFilters(): void {
    this.availableFilters = [
      {
        name: 'payment_status',
        options: this.paymentStatusOptions.map(opt => opt.label),
        defaultValue: ''
      },
      {
        name: 'reconciliation_status',
        options: this.reconciliationStatusOptions.map(opt => opt.label),
        defaultValue: ''
      }
    ];
  }

  private setupDataChangeSubscription(): void {
    const subscription = this.dataChangeService.dataChanged$.subscribe(() => {
      this.loadData();
    });
    this.subscriptions.push(subscription);
  }

  /**
   * The base reconciliation-status filter implied by the active tab.
   * - import:    only OFFEN (includes not-yet-assigned payments)
   * - zahlungen: everything that already has a status other than offen
   */
  private tabReconciliationStatuses(): ReconciliationStatus[] {
    if (this.activeTab === 'import') {
      return [ReconciliationStatus.OFFEN];
    }
    return [
      ReconciliationStatus.GEPRÜFT,
      ReconciliationStatus.ABGESCHLOSSEN,
      ReconciliationStatus.PROBLEM,
      ReconciliationStatus.ZURÜCKGEBUCHT,
    ];
  }

  setTab(tab: 'import' | 'zahlungen'): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.pageIndex = 0;
    this.searchValue = '';
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;

    // On the Zahlungen tab the user can narrow within the non-open statuses via
    // the filter; if they pick none we fall back to the tab's base status set.
    const formRecStatus = this.filterForm.get('reconciliationStatus')?.value || [];
    const reconciliationStatus =
      this.activeTab === 'zahlungen' && formRecStatus.length
        ? formRecStatus
        : this.tabReconciliationStatuses();

    const filters: ZahlungFilter = {
      dateFrom: this.toIsoDate(this.filterForm.get('dateFrom')?.value),
      dateTo: this.toIsoDate(this.filterForm.get('dateTo')?.value),
      paymentStatus: this.filterForm.get('paymentStatus')?.value,
      reconciliationStatus: reconciliationStatus,
      amountMin: this.filterForm.get('amountMin')?.value,
      amountMax: this.filterForm.get('amountMax')?.value,
      debtorName: this.filterForm.get('debtorName')?.value,
      aktenzeichen: this.filterForm.get('aktenzeichen')?.value
    };

    const subscription = this.zahlungsabgleichService
      .getZahlungen(
        this.pageIndex + 1,
        this.pageSize,
        this.sortField,
        this.sortOrder,
        this.searchValue,
        filters
      )
      .subscribe({
        next: (response: ZahlungTable) => {
          this.zahlungen = response.data;
          this.dataSource.data = this.zahlungen;
          this.totalCount = response.total;
          this.totalPages = response.total_pages;
          this.summary = response.summary;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading data:', error);
          this.isLoading = false;
          this.snackBar.open('Fehler beim Laden der Daten', 'Schließen', { duration: 3000 });
        }
      });

    this.subscriptions.push(subscription);
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue.trim().toLowerCase();
    this.pageIndex = 0;
    this.loadData();
  }

  onPageChange(event: any): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData();
  }

  onSortChange(event: any): void {
    this.sortField = event.active;
    this.sortOrder = event.direction;
    this.loadData();
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.loadData();
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.searchValue = '';
    this.pageIndex = 0;
    this.loadData();
  }

  toggleFilter(): void {
    this.isFilterExpanded = !this.isFilterExpanded;
  }

  editZahlung(zahlung: Zahlung): void {
    this.selectedZahlung = zahlung;
    this.current_aktenzeichen = zahlung.aktenzeichen || '';
    
    // Show offcanvas
    const offcanvasElement = document.getElementById('staticBackdrop');
    if (offcanvasElement && (window as any).bootstrap?.Offcanvas) {
      const offcanvas = (window as any).bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement);
      // When closed via X / Escape / backdrop, clear the child route so the
      // detail can be reopened cleanly and the list reflects any changes.
      offcanvasElement.addEventListener(
        'hidden.bs.offcanvas',
        () => {
          if (this.router.url.includes('/zahlungsabgleich/edit')) {
            this.router.navigate(['/zahlungsabgleich']);
          }
        },
        { once: true }
      );
      offcanvas.show();
    }

    // Navigate to edit component
    this.router.navigate(['/zahlungsabgleich/edit'], {
      queryParams: { id: zahlung.id }
    });
  }

  reconcileZahlung(zahlung: Zahlung): void {
    this.zahlungsabgleichService.reconcilePayment(zahlung.id).subscribe({
      next: (result) => {
        this.snackBar.open(result.message, 'Schließen', { duration: 3000 });
        this.loadData();
      },
      error: (error) => {
        console.error('Error reconciling zahlung:', error);
        this.snackBar.open('Fehler beim Abgleichen der Zahlung', 'Schließen', { duration: 3000 });
      }
    });
  }

  autoMatchPayments(): void {
    this.isLoading = true;
    this.zahlungsabgleichService.autoMatchPayments().subscribe({
      next: (results) => {
        this.snackBar.open(`${results.length} Zahlungen automatisch zugeordnet`, 'Schließen', { duration: 3000 });
        this.loadData();
      },
      error: (error) => {
        console.error('Error auto-matching payments:', error);
        this.snackBar.open('Fehler beim automatischen Zuordnen', 'Schließen', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  // ---------------------------------------------------------------------------
  //  IMPORT (file picker, drag-drop, inbox sync)
  // ---------------------------------------------------------------------------
  dropActive = false;
  private readonly importAccept = /\.(dat|xml|zip)$/i;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropActive = false;
    const files = Array.from(event.dataTransfer?.files ?? []).filter(f => this.importAccept.test(f.name));
    if (files.length) {
      this.uploadFiles(files, 'Drop');
    } else {
      this.snackBar.open('Nur .dat, .xml oder .zip Dateien werden unterstützt', 'Schließen', { duration: 3000 });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []).filter(f => this.importAccept.test(f.name));
    if (files.length) {
      this.uploadFiles(files, 'Manual');
    }
    input.value = '';
  }

  private uploadFiles(files: File[], source: 'Manual' | 'Drop' | 'Sync'): void {
    this.isLoading = true;
    this.zahlungsabgleichService.importFiles(files, source).subscribe({
      next: (res: any) => {
        this.showImportSummary(res?.summary);
        this.loadData();
      },
      error: (error) => {
        console.error('Error importing files:', error);
        this.snackBar.open('Fehler beim Importieren der Dateien', 'Schließen', { duration: 4000 });
        this.isLoading = false;
      }
    });
  }

  syncFolder(): void {
    this.isLoading = true;
    this.zahlungsabgleichService.syncInbox().subscribe({
      next: (res: any) => {
        this.showImportSummary(res?.summary);
        this.loadData();
      },
      error: (error) => {
        console.error('Error syncing inbox:', error);
        this.snackBar.open('Fehler bei der Ordner-Synchronisation', 'Schließen', { duration: 4000 });
        this.isLoading = false;
      }
    });
  }

  private showImportSummary(summary: any): void {
    if (!summary) {
      this.snackBar.open('Import abgeschlossen', 'Schließen', { duration: 3000 });
      return;
    }
    const msg = `Import: ${summary.imported ?? 0} neu, ${summary.skipped_duplicate ?? 0} Duplikate (übersprungen), ${summary.failed ?? 0} Fehler`;
    this.snackBar.open(msg, 'Schließen', { duration: 6000 });
  }

  // ---------------------------------------------------------------------------
  //  BERICHT (payment report -> HTML preview + PDF)
  // ---------------------------------------------------------------------------
  openReportDialog(): void {
    this.dialog.open(ZahlungReportDialogComponent, {
      maxWidth: '95vw',
      width: '1100px',
      panelClass: 'zahlung-report-dialog-panel',
      data: {
        dateFrom: this.toIsoDate(this.filterForm.get('dateFrom')?.value),
        dateTo: this.toIsoDate(this.filterForm.get('dateTo')?.value),
        aktenzeichen: this.filterForm.get('aktenzeichen')?.value || '',
      },
    });
  }

  // ---------------------------------------------------------------------------
  //  RETOUR (return payments -> camt.054)
  // ---------------------------------------------------------------------------
  exportData(): void {
    const filters: ZahlungFilter = {
      dateFrom: this.toIsoDate(this.filterForm.get('dateFrom')?.value),
      dateTo: this.toIsoDate(this.filterForm.get('dateTo')?.value),
      paymentStatus: this.filterForm.get('paymentStatus')?.value,
      reconciliationStatus: this.filterForm.get('reconciliationStatus')?.value
    };

    this.zahlungsabgleichService.exportReconciliationReport(filters).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `zahlungsabgleich_${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error exporting data:', error);
        this.snackBar.open('Fehler beim Exportieren der Daten', 'Schließen', { duration: 3000 });
      }
    });
  }

  getPaymentStatusLabel(status: PaymentStatus): string {
    const option = this.paymentStatusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  }

  getReconciliationStatusLabel(status: ReconciliationStatus): string {
    const option = this.reconciliationStatusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  }

  getPaymentStatusClass(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.VOLLSTAENDIG:
        return 'status-success';
      case PaymentStatus.TEILWEISE:
        return 'status-warning';
      case PaymentStatus.ÜBERZAHLT:
      case PaymentStatus.UNTERZAHLT:
      case PaymentStatus.DOPPELT:
        return 'status-danger';
      case PaymentStatus.STORNIERT:
      case PaymentStatus.ZURÜCKGEBUCHT:
        return 'status-secondary';
      default:
        return 'status-info';
    }
  }

  getReconciliationStatusClass(status: ReconciliationStatus): string {
    switch (status) {
      case ReconciliationStatus.ABGESCHLOSSEN:
        return 'status-success';
      case ReconciliationStatus.GEPRÜFT:
        return 'status-warning';
      case ReconciliationStatus.PROBLEM:
        return 'status-danger';
      case ReconciliationStatus.ZURÜCKGEBUCHT:
        return 'status-secondary';
      default:
        return 'status-info';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE');
  }
}
